import {
  type BodyMapReport,
  type IncidentReport,
  IncidentStatus
} from '@lmc/ts-common'
import {
  CONTRIBUTING_FACTOR_GROUPS,
  type ContributingFactorEnum,
  type ContributingFactorGroupEnum,
  type DutyOfCandour,
  DutyOfCandourKeyRequirementEnum,
  DutyOfCandourMethodEnum,
  DutyOfCandourPersonInformedEnum,
  type FollowUpActionEntry,
  type IncidentReportFetchByLogIdResponse,
  InjuredPersonTypeEnum,
  type Injury,
  type ResidentInjuryDetailsRequest,
  type RestrictivePractice,
  type RestrictivePractices
} from '@lmc/validations'
import type { TFunction } from 'i18next'
import { incidentReportsConstants } from 'locale/incidentReports'
import { isOverdue } from '../steps/followUpActions/followUpActionValues'
import {
  type CaseImmediateActions,
  CHECKLIST
} from '../steps/ImmediateActionsStep'
import type { Step5Case } from '../steps/injuriesAndDamages/appInjuries'
import { GROUP_ORDER } from '../steps/investigation/ContributingFactors'
import type { DebriefKind } from '../steps/investigation/DebriefFields'
import { PARTIES } from '../steps/NotificationsStep'
import { ENQUIRY_OUTCOMES } from '../steps/notifications/SafeguardingEnquiryFields'
import { type SavedPerson, savedNames } from '../steps/PeopleInvolvedStep'
import {
  type AppRestraintAnswers,
  restrictivePracticesFromApp
} from '../steps/restrictivePractices/appRestraints'
import {
  INTERVENTION_TYPE_KEYS,
  keyFor,
  OUTCOME_KEYS,
  METHOD_KEYS as PRACTICE_METHOD_KEYS,
  PRACTICE_TYPE_KEYS
} from '../steps/restrictivePractices/restrictivePracticeOptions'
import { savedNotifiedParties } from '../stepValues'
import {
  formatDateOnly,
  formatDateTime,
  formatTimeRange,
  yesNoKey
} from './readValues'
import type { PersonName } from './usePersonName'

/*
 * What each step's answers read as, shared by the case view and its PDF: which
 * answers show, in what order, and their display text. Layout stays with the
 * view; labels are the locale keys both use.
 */

/** A translated yes/no answer, or nothing when unanswered. */
export const yesNo = (t: TFunction, value: boolean | null | undefined) => {
  const key = yesNoKey(value)

  return key ? t(key) : null
}

/** One space between words: the read keeps saved spacing, which may be doubled. */
export const singleSpaced = (text: string) => text.replace(/\s+/g, ' ').trim()

/**
 * A saved person's name: the one the read embeds, else the one looked up,
 * else `notFound`, so a person who was recorded never reads as unrecorded.
 * Nothing without an id.
 */
export const savedPersonName = ({
  embeddedName,
  id,
  names,
  notFound
}: {
  embeddedName?: unknown
  id: string | null | undefined
  names: ReadonlyMap<string, string>
  notFound: string
}) => {
  if (typeof embeddedName === 'string' && embeddedName.trim()) {
    return singleSpaced(embeddedName)
  }
  return id ? names.get(id) || notFound : null
}

/** Names for ids, leaving out any the lookup can't name. */
export const namesOfIds = (
  ids: ReadonlyArray<string | null | undefined> | null | undefined,
  nameOf: PersonName
) =>
  (ids ?? []).flatMap((id) => {
    const name = nameOf(id)

    return name ? [name] : []
  })

// Step 1: Incident information

/**
 * The case metadata the read returns; ts-common's report type lacks it. An
 * earlier build also saved an end time, which the incident's one time ignores.
 */
export type CaseMetadata = {
  caseOwner?: { name?: string } | null
  caseReference?: string | null
  incidentOccurredAt?: string | null
}

/** Sentence case, as the design's status badge. */
export const STATUS_LABEL_KEYS: Record<IncidentStatus, string> = {
  [IncidentStatus.closed]: incidentReportsConstants.CASE_V2_VIEW_STATUS_CLOSED,
  [IncidentStatus.needsReview]:
    incidentReportsConstants.CASE_V2_VIEW_STATUS_NEEDS_REVIEW,
  [IncidentStatus.open]: incidentReportsConstants.CASE_V2_VIEW_STATUS_OPEN
}

/** Who closed the incident and why, shown only once it is closed. */
export const closure = ({
  closeComment,
  closedBy,
  status
}: Pick<IncidentReport, 'closeComment' | 'closedBy' | 'status'>) =>
  status === IncidentStatus.closed
    ? { closeComment, closedBy: closedBy?.name }
    : null

/** The handover answer's key; an answered No reads as No, not "Not recorded". */
export const handoverKey = (isNextHandover: boolean | null | undefined) =>
  isNextHandover === undefined || isNextHandover === null
    ? null
    : isNextHandover
      ? incidentReportsConstants.CASE_V2_INFO_FLAG_FOR_HANDOVER
      : incidentReportsConstants.CASE_V2_NO

// Step 2: Immediate actions

/**
 * The ticked checklist items in checklist order, then any saved action the
 * checklist doesn't know, as saved. Four items are notified parties; other
 * parties the Notifications step adds are not listed.
 */
export const immediateActionLabels = (
  t: TFunction,
  incidentReport: IncidentReport
) => {
  const actions: Array<string> =
    (incidentReport.case as CaseImmediateActions | null)?.immediateActions ?? []
  const ticked = new Set<string>([
    ...actions,
    ...savedNotifiedParties(incidentReport).map(({ party }) => party)
  ])
  const unknown = actions.filter(
    (action) => !CHECKLIST.some(({ value }) => value === action)
  )

  return [
    ...CHECKLIST.filter(({ value }) => ticked.has(value)).map(({ labelKey }) =>
      t(labelKey)
    ),
    ...unknown
  ]
}

// Step 3: People involved

/** Each saved person's name, the one the read embeds first. */
export const toNames = (
  people: Array<SavedPerson> | null | undefined,
  nameOf: PersonName
) =>
  (people ?? []).flatMap((person) => {
    const name = person && nameOf(person.id, person.name)

    return name ? [name] : []
  })

/** Named entries, with the old free text in their place as the form shows it. */
export const toEntryNames = (
  ...args: Parameters<typeof savedNames>
): Array<string> =>
  savedNames(...args).flatMap(({ name }) => (name?.trim() ? [name.trim()] : []))

/** The case's own service user, with the name staff use when there is one. */
export const serviceUserName = (
  t: TFunction,
  name: string,
  preferredName: string | null | undefined
) =>
  preferredName
    ? t(incidentReportsConstants.CASE_V2_VIEW_PREFERRED_NAME, {
        name,
        preferredName
      })
    : name

// Step 4: Restrictive practices

/** What the read returns that ts-common's report type lacks. */
type RestrictiveCase = {
  case?:
    | ({ restrictivePractices?: RestrictivePractices } & AppRestraintAnswers)
    | null
}

/** Step 4's answers; until the step is saved, what the app recorded. */
export const savedRestrictivePractices = (incidentReport: IncidentReport) => {
  const savedCase = (incidentReport as IncidentReport & RestrictiveCase).case

  return (
    savedCase?.restrictivePractices ?? restrictivePracticesFromApp(savedCase)
  )
}

/** Why none were used, shown only under No. */
export const restrictiveDetails = (
  saved: ReturnType<typeof savedRestrictivePractices>
) =>
  saved?.hasBeenApplied === false && saved.details?.trim()
    ? saved.details
    : null

/** The practices recorded, shown only under Yes. */
export const recordedPractices = (
  saved: ReturnType<typeof savedRestrictivePractices>
): Array<RestrictivePractice> =>
  saved?.hasBeenApplied ? (saved.entries ?? []) : []

/** One practice's answers as they read. */
export const practiceValues = (
  t: TFunction,
  practice: RestrictivePractice
) => ({
  deEscalation: yesNo(t, practice.deEscalationAttempted),
  duration: formatTimeRange(t, practice.startedAt, practice.endedAt),
  interventionType: practice.interventionType
    ? t(keyFor(INTERVENTION_TYPE_KEYS, practice.interventionType))
    : null,
  method: practice.method
    ? t(keyFor(PRACTICE_METHOD_KEYS, practice.method))
    : null,
  outcomes: practice.outcomes?.map((outcome) =>
    t(keyFor(OUTCOME_KEYS, outcome))
  ),
  type: t(keyFor(PRACTICE_TYPE_KEYS, practice.type))
})

// Step 5: Injuries and damages

type Person = { id: string; name: string } | null

/** The case fields this step saves; ts-common's case type lacks most of them. */
export type InjuriesCase = Step5Case & {
  carersInvolved?: Array<Person>
  residentInjuries?: ResidentInjuryDetailsRequest | null
  serviceUsersInvolved?: Array<Person>
}

/** The order the form groups injuries in. */
const PERSON_TYPES = [
  InjuredPersonTypeEnum.serviceUser,
  InjuredPersonTypeEnum.otherServiceUser,
  InjuredPersonTypeEnum.teamMember,
  InjuredPersonTypeEnum.otherParty
] as const

/** A yes/no answer's details, shown only under No. */
export const detailsUnderNo = (
  answer:
    | { details?: string | null; hasBeenApplied?: boolean }
    | null
    | undefined
) => (answer?.hasBeenApplied === false ? answer.details : null)

/** The injuries recorded under Yes, grouped by person and numbered within each. */
export const injuryEntries = (
  injuries:
    | { entries?: Array<Injury>; hasBeenApplied?: boolean }
    | null
    | undefined
) =>
  injuries?.hasBeenApplied
    ? PERSON_TYPES.flatMap((personType) =>
        (injuries.entries ?? [])
          .filter((entry) => entry.personType === personType)
          .map((entry, index) => ({ entry, number: index + 1 }))
      )
    : []

/** The damage recorded under Yes. */
export const recordedDamages = <Entry>(
  damages:
    | { entries?: Array<Entry>; hasBeenApplied?: boolean }
    | null
    | undefined
): Array<Entry> => (damages?.hasBeenApplied ? (damages.entries ?? []) : [])

/** The names the read embeds for People involved, which name injuries first. */
export const embeddedNames = (savedCase: InjuriesCase) =>
  new Map(
    [
      ...(savedCase.serviceUsersInvolved ?? []),
      ...(savedCase.carersInvolved ?? [])
    ].flatMap(
      (person): Array<[string, string]> =>
        person ? [[person.id, person.name]] : []
    )
  )

/** An injury's heading: the service user by name, anyone else numbered. */
export const injuryTitle = (
  t: TFunction,
  entry: Injury,
  number: number,
  {
    serviceUser,
    serviceUserWord
  }: { serviceUser: string; serviceUserWord: string }
) => {
  switch (entry.personType) {
    case InjuredPersonTypeEnum.serviceUser:
      return serviceUser
    case InjuredPersonTypeEnum.otherServiceUser:
      return t(
        incidentReportsConstants.CASE_V2_VIEW_INJURIES_OTHER_SERVICE_USER,
        { number, variable: serviceUserWord }
      )
    case InjuredPersonTypeEnum.teamMember:
      return t(incidentReportsConstants.CASE_V2_VIEW_INJURIES_TEAM_MEMBER, {
        number
      })
    default:
      return t(incidentReportsConstants.CASE_V2_VIEW_INJURIES_OTHER_PARTY, {
        number
      })
  }
}

/**
 * Who an injury names: another service user or a team member by id, anyone
 * else by the name typed. The case's own service user heads the entry instead.
 */
export const injuredPerson = (
  entry: Injury
): {
  id?: string | null
  lookup: 'serviceUser' | 'teamMember' | null
  name?: string | null
} | null =>
  entry.personType === InjuredPersonTypeEnum.otherServiceUser
    ? { id: entry.residentId, lookup: 'serviceUser' }
    : entry.personType === InjuredPersonTypeEnum.teamMember
      ? { id: entry.userId, lookup: 'teamMember' }
      : entry.personType === InjuredPersonTypeEnum.serviceUser
        ? null
        : { lookup: null, name: entry.name }

/** The body maps linked to an injury: the service user's only. */
export const injuryBodyMapIds = (entry: Injury, savedCase: InjuriesCase) =>
  entry.personType === InjuredPersonTypeEnum.serviceUser
    ? (savedCase.residentInjuries?.residentInjuriesBodyMaps ?? [])
    : []

/** A linked body map, or its id with nothing to link when it no longer resolves. */
export const linkedBodyMap = (
  t: TFunction,
  id: string,
  bodyMaps: ReadonlyArray<BodyMapReport>
) => {
  const bodyMap = bodyMaps.find((map) => String(map.id) === id)

  return {
    bodyMap,
    label: bodyMap
      ? bodyMap.title ||
        formatDateTime(bodyMap.createdOn) ||
        t(incidentReportsConstants.CASE_V2_VIEW_BODY_MAP_UNTITLED)
      : t(incidentReportsConstants.CASE_V2_VIEW_BODY_MAP_NOT_FOUND)
  }
}

// Step 6: Follow-up actions

/** The saved follow-up as the read returns it; absent until the step is saved. */
type SavedFollowUp = {
  followUpActions?: { actions?: Array<FollowUpActionEntry> } | null
}

export const savedFollowUpActions = (incidentReport: IncidentReport) =>
  (incidentReport.case as SavedFollowUp | null)?.followUpActions?.actions ?? []

/** An action's due date, flagged overdue when it is past and still open. */
export const actionDueDate = (t: TFunction, action: FollowUpActionEntry) => {
  const date = formatDateOnly(action.dueDate)

  return date && isOverdue(action)
    ? {
        isOverdue: true,
        text: t(incidentReportsConstants.CASE_V2_ACTIONS_OVERDUE, { date })
      }
    : { isOverdue: false, text: date }
}

// Step 7: Investigation

type ReadCase = NonNullable<IncidentReportFetchByLogIdResponse['case']>

/** The investigation fields the read returns; ts-common's case type lacks them. */
export type InvestigationCase = Partial<
  Pick<
    ReadCase,
    | 'coldDebrief'
    | 'contributingFactors'
    | 'contributingFactorsExplanation'
    | 'debriefRequired'
    | 'hotDebrief'
    | 'immediateCause'
    | 'lessonsIdentified'
    | 'serviceUserOpinion'
  >
>

export type Debrief = NonNullable<ReadCase['coldDebrief']>

/**
 * Whether a debrief was needed. An unticked box saves as false whether or not
 * anyone looked at it, so a No shows only beside debriefs it overrides; Yes
 * always shows.
 */
export const debriefRequiredKey = (saved: InvestigationCase) =>
  saved.debriefRequired || saved.hotDebrief || saved.coldDebrief
    ? yesNoKey(saved.debriefRequired)
    : null

/** The debriefs recorded, hot then cold, while a debrief is required. */
export const recordedDebriefs = (
  saved: InvestigationCase
): Array<{ debrief: Debrief; kind: DebriefKind }> =>
  saved.debriefRequired
    ? (['hotDebrief', 'coldDebrief'] as const).flatMap((kind) => {
        const debrief = saved[kind]

        return debrief ? [{ debrief, kind }] : []
      })
    : []

export type TickedFactorGroup = {
  group: ContributingFactorGroupEnum
  ticked: ReadonlyArray<ContributingFactorEnum>
}

/**
 * The groups with a ticked factor, in the form's order. A factor the form has
 * no group for is left out, so none known reads as not recorded, not blank.
 */
export const tickedFactorGroups = (
  factors: ReadonlyArray<ContributingFactorEnum>
): Array<TickedFactorGroup> =>
  GROUP_ORDER.map((group) => ({
    group,
    ticked: CONTRIBUTING_FACTOR_GROUPS[group].filter((factor) =>
      factors.includes(factor)
    )
  })).filter(({ ticked }) => ticked.length > 0)

/** A debrief's heading and notes label; only the cold one has improvement actions. */
export const DEBRIEF_KEYS: Record<
  DebriefKind,
  { hasImprovementActions: boolean; notesKey: string; titleKey: string }
> = {
  coldDebrief: {
    hasImprovementActions: true,
    notesKey:
      incidentReportsConstants.CASE_V2_INVESTIGATION_LEARNING_IDENTIFIED,
    titleKey: incidentReportsConstants.CASE_V2_INVESTIGATION_COLD_DEBRIEF
  },
  hotDebrief: {
    hasImprovementActions: false,
    notesKey: incidentReportsConstants.CASE_V2_INVESTIGATION_IMMEDIATE_LEARNING,
    titleKey: incidentReportsConstants.CASE_V2_INVESTIGATION_HOT_DEBRIEF
  }
}

// Step 8: Duty of Candour

const CANDOUR_KEY_REQUIREMENT_KEYS: Record<
  DutyOfCandourKeyRequirementEnum,
  string
> = {
  [DutyOfCandourKeyRequirementEnum.apologyProvided]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_APOLOGY_PROVIDED,
  [DutyOfCandourKeyRequirementEnum.explanationGiven]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_EXPLANATION_GIVEN,
  [DutyOfCandourKeyRequirementEnum.supportOffered]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_SUPPORT_OFFERED,
  [DutyOfCandourKeyRequirementEnum.writtenFollowUpProvided]:
    incidentReportsConstants.CASE_V2_CANDOUR_REQUIREMENT_WRITTEN_FOLLOW_UP_PROVIDED
}

const CANDOUR_METHOD_KEYS: Record<DutyOfCandourMethodEnum, string> = {
  [DutyOfCandourMethodEnum.inPersonMeeting]:
    incidentReportsConstants.CASE_V2_CANDOUR_METHOD_IN_PERSON_MEETING,
  [DutyOfCandourMethodEnum.other]:
    incidentReportsConstants.CASE_V2_CANDOUR_OTHER,
  [DutyOfCandourMethodEnum.phone]:
    incidentReportsConstants.CASE_V2_CANDOUR_METHOD_PHONE,
  [DutyOfCandourMethodEnum.written]:
    incidentReportsConstants.CASE_V2_CANDOUR_METHOD_WRITTEN
}

// The form's order, whatever order the answers were saved in.
const KEY_REQUIREMENT_ORDER = [
  DutyOfCandourKeyRequirementEnum.apologyProvided,
  DutyOfCandourKeyRequirementEnum.explanationGiven,
  DutyOfCandourKeyRequirementEnum.supportOffered,
  DutyOfCandourKeyRequirementEnum.writtenFollowUpProvided
]
const METHOD_ORDER = [
  DutyOfCandourMethodEnum.phone,
  DutyOfCandourMethodEnum.inPersonMeeting,
  DutyOfCandourMethodEnum.written,
  DutyOfCandourMethodEnum.other
]

type Communication = DutyOfCandour['communication']

/** `dutyOfCandour` is not on the ts-common case type. */
export const savedDutyOfCandour = (incidentReport: IncidentReport) =>
  (incidentReport.case as { dutyOfCandour?: DutyOfCandour } | null)
    ?.dutyOfCandour

/** Why Duty of Candour doesn't apply, shown only under No. */
export const candourDetails = (saved: DutyOfCandour | undefined) =>
  saved?.isApplicable === false ? saved.details : null

/** The trimmed, non-blank names of a name list. */
const namesOf = (people: Array<{ name?: string }> | undefined) =>
  (people ?? [])
    .map((person) => person.name?.trim())
    .filter((name): name is string => Boolean(name))

/** Who was informed, by name where names were recorded. */
export const personsInformedLabels = (
  t: TFunction,
  saved: DutyOfCandour,
  serviceUserWord: string
) => {
  const informed = (person: DutyOfCandourPersonInformedEnum) =>
    Boolean(saved.personsInformed?.includes(person))
  const familyNames = namesOf(saved.familyRepresentatives)
  const otherNames = namesOf(saved.otherPersons)

  return [
    ...(informed(DutyOfCandourPersonInformedEnum.serviceUser)
      ? [serviceUserWord]
      : []),
    ...(informed(DutyOfCandourPersonInformedEnum.familyOrRepresentative)
      ? familyNames.length
        ? familyNames.map((name) =>
            t(incidentReportsConstants.CASE_V2_VIEW_CANDOUR_FAMILY_NAMED, {
              name
            })
          )
        : [t(incidentReportsConstants.CASE_V2_CANDOUR_PERSON_FAMILY)]
      : []),
    ...(informed(DutyOfCandourPersonInformedEnum.other)
      ? otherNames.length
        ? otherNames.map((name) =>
            t(incidentReportsConstants.CASE_V2_VIEW_CANDOUR_OTHER_NAMED, {
              name
            })
          )
        : [t(incidentReportsConstants.CASE_V2_CANDOUR_OTHER)]
      : [])
  ]
}

/** The methods used in the form's order, "Other" with what it was. */
export const candourMethodLabels = (
  t: TFunction,
  communication: Communication
) => {
  const otherMethod = communication?.otherMethod?.trim()

  return METHOD_ORDER.filter((method) =>
    communication?.methods?.includes(method)
  ).map((method) =>
    method === DutyOfCandourMethodEnum.other && otherMethod
      ? t(incidentReportsConstants.CASE_V2_VIEW_CANDOUR_OTHER_METHOD, {
          method: otherMethod
        })
      : t(CANDOUR_METHOD_KEYS[method])
  )
}

/** The key requirements met, in the form's order. */
export const keyRequirementLabels = (
  t: TFunction,
  communication: Communication
) =>
  KEY_REQUIREMENT_ORDER.filter((requirement) =>
    communication?.keyRequirementsMet?.includes(requirement)
  ).map((requirement) => t(CANDOUR_KEY_REQUIREMENT_KEYS[requirement]))

/** The written follow-up's date is asked only once that box is ticked. */
export const hasWrittenFollowUp = (communication: Communication) =>
  Boolean(
    communication?.keyRequirementsMet?.includes(
      DutyOfCandourKeyRequirementEnum.writtenFollowUpProvided
    )
  )

// Step 9: Notifications

/**
 * The parties this step records that were notified, in the form's order. The
 * GP and district nurse belong to Immediate actions.
 */
export const notifiedPartyEntries = (incidentReport: IncidentReport) => {
  const saved = savedNotifiedParties(incidentReport)

  return PARTIES.flatMap((definition) => {
    const entry = saved.find(({ party }) => party === definition.party)

    return entry ? [{ definition, entry }] : []
  })
}

type NotifiedPartyEntry = ReturnType<typeof notifiedPartyEntries>[number]

/** A safeguarding enquiry's details show once the referral progressed to one. */
export const showsEnquiry = ({ definition, entry }: NotifiedPartyEntry) =>
  Boolean(definition.hasEnquiry && entry.progressedToEnquiry)

/** An enquiry outcome's label key, or none for an outcome the app doesn't know. */
export const enquiryOutcomeKey = ({ entry }: NotifiedPartyEntry) =>
  ENQUIRY_OUTCOMES.find(({ outcome }) => outcome === entry.enquiryOutcome)
    ?.labelKey ?? null
